import { useState, useEffect } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import "./DocumentationPage.css";
import { addToDiscord } from "../utils/discord";

export default function DocumentationPage() {
  const [activeSection, setActiveSection] = useState("getting-started");

  const sections = [
    { id: "getting-started", label: "Getting Started", icon: "🚀" },
    { id: "security", label: "Security & API Keys", icon: "🔐" },
    { id: "setup", label: "Setup Guide", icon: "⚙️" },
    { id: "commands", label: "Discord Commands", icon: "💬" },
    { id: "providers", label: "API Providers", icon: "🔌" },
    { id: "models", label: "Managing Models", icon: "🤖" },
    { id: "usage", label: "Usage & Costs", icon: "📊" },
    { id: "memory", label: "Memory", icon: "🧠" },
    { id: "features", label: "Advanced Features", icon: "✨" },
    { id: "troubleshooting", label: "Troubleshooting", icon: "🔧" },
  ];

  function scrollToSection(id) {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  }

  // On mount: if there is a hash (#section), go there; otherwise force scroll to top.
  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (hash) {
      // Defer to ensure DOM is painted
      setTimeout(() => scrollToSection(hash), 0);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash?.replace("#", "");
      if (hash) {
        setTimeout(() => scrollToSection(hash), 0);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div className="docs-page">
      {/* Navigation */}
      <SiteNav showCta />

      {/* Documentation Header */}
      <div className="docs-header">
        <div className="docs-header-content">
          <h1 className="docs-title">Documentation</h1>
          <p className="docs-subtitle">
            Everything you need to know about using OkeyBot
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="docs-container">
        {/* Sidebar */}
        <aside className="docs-sidebar">
          <nav className="docs-nav">
            <div className="docs-nav-title">Contents</div>
            {sections.map((section) => (
              <button
                key={section.id}
                className={`docs-nav-item ${activeSection === section.id ? "active" : ""}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="docs-nav-icon">{section.icon}</span>
                {section.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="docs-content">
          {/* Getting Started */}
          <section id="getting-started" className="docs-section">
            <h2 className="docs-section-title">🚀 Getting Started</h2>
            <p className="docs-text">
              Welcome to OkeyBot! This guide will help you get up and running in just a few minutes.
            </p>

            <div className="docs-card">
              <h3 className="docs-card-title">What is OkeyBot?</h3>
              <p className="docs-text">
                OkeyBot is a Discord bot that lets you chat with multiple AI providers (OpenAI, Anthropic, Google, xAI, Groq, Perplexity, OpenRouter) using your own API keys. You maintain full control over your costs, data, and privacy.
              </p>
              <div className="docs-note" style={{ marginTop: "1rem" }}>
                <strong>Portable Account:</strong> Your API keys and settings
                are tied to your Discord account. You can invite the bot to
                your own personal server for private use, or use it in any server
                where the bot is present.
              </div>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Key Features</h3>
              <ul className="docs-list">
                <li>Support for 7 major AI providers</li>
                <li>Use your own API keys - no markup fees</li>
                <li>Real-time usage tracking and cost analytics</li>
                <li>Switch between models on the fly</li>
                <li>Native Discord threading for organized conversations</li>
                <li>Vision models support (images)</li>
                <li>Streaming responses for faster interactions</li>
                <li>Attach text/code files to your prompts</li>
              </ul>
            </div>
          </section>
          {/* Security & API Keys */}
          <section id="security" className="docs-section">
            <h2 className="docs-section-title">🔐 Security & API Keys</h2>
            <div className="docs-card">
              <h3 className="docs-card-title">How we use your key</h3>
              <ul className="docs-list">
                <li><strong>Encrypted at rest:</strong> Your API keys are encrypted and stored server‑side. We only decrypt them when needed to fulfill your requests.</li>
                <li><strong>Used only on your behalf:</strong> We use your key solely to call the provider models you enable, when you run a command or a reply is generated for you.</li>
                <li><strong>Not shared:</strong> Keys are never exposed to other users, never sent to Discord, and never shared with third parties.</li>
                <li><strong>Per‑provider:</strong> You keep one key per provider. You can remove or replace keys at any time in the dashboard.</li>
              </ul>
              <div className="docs-note">
                We do not store your conversation content in our database. Messages live in Discord. We retain minimal usage metadata necessary to operate the service and provide analytics (for example, usage and cost tracking).
              </div>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Access control</h3>
              <ul className="docs-list">
                <li><strong>Thread Mode (Account):</strong> Inside <code>/chat</code> threads, your personal API key is used. Only you can prompt the bot unless you enable “Allow others” in Settings.</li>
                <li><strong>Mention Mode (Server):</strong> When someone @mentions the bot in a server, it uses the <strong>Server Owner's</strong> mapped model and API budget.</li>
                <li><strong>Owner-only controls:</strong> Only the thread owner can run <code>/usage</code> for a thread. Changing the thread's model is also restricted to the owner if "Allow others" is off.</li>
                <li><strong>Deletion:</strong> Remove keys on the API Keys page, or delete your account to purge keys, models, usage logs, and thread settings.</li>
              </ul>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Transparency & control</h3>
              <ul className="docs-list">
                <li>Use <code>/usage</code> in Discord for thread/account usage.</li>
                <li>See detailed breakdowns in <a href="/dashboard/usage" target="_self" rel="noopener noreferrer">Dashboard → Usage</a>.</li>
                <li>Manage keys in <a href="/dashboard/keys" target="_self" rel="noopener noreferrer">Dashboard → API Keys</a>.</li>
              </ul>
              <p className="docs-text" style={{ marginTop: "0.5rem" }}>
                Note: Provider policies vary. Most major providers do not use API data to train their models by default, but always review your provider’s settings.
              </p>
            </div>
          </section>

          {/* Setup Guide */}
          <section id="setup" className="docs-section">
            <h2 className="docs-section-title">⚙️ Setup Guide</h2>

            <div className="docs-step">
              <div className="docs-step-number">1</div>
              <div className="docs-step-content">
                <h3 className="docs-step-title">Add Bot to Discord</h3>
                <p className="docs-text">
                  Click the "Add to Discord" button on our homepage and select the server where you want to use OkeyBot. The bot needs permissions to:
                </p>
                <ul className="docs-list">
                  <li>Read and send messages</li>
                  <li>Create and manage threads</li>
                  <li>Use slash commands</li>
                </ul>
              </div>
            </div>

            <div className="docs-step">
              <div className="docs-step-number">2</div>
              <div className="docs-step-content">
                <h3 className="docs-step-title">Login to Dashboard</h3>
                <p className="docs-text">
                  After adding the bot, log in to the OkeyBot dashboard using your Discord account. This is where you'll manage your API keys and model settings.
                </p>
              </div>
            </div>

            <div className="docs-step">
              <div className="docs-step-number">3</div>
              <div className="docs-step-content">
                <h3 className="docs-step-title">Add API Keys</h3>
                <p className="docs-text">
                  Navigate to the "API Keys" page and add keys from your preferred AI providers:
                </p>
                <ul className="docs-list">
                  <li><strong>OpenAI:</strong> Get your key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a></li>
                  <li><strong>Anthropic:</strong> Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com/settings/keys</a></li>
                  <li><strong>Google:</strong> Get your key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com/app/apikey</a></li>
                  <li><strong>xAI:</strong> Get your key from <a href="https://console.x.ai" target="_blank" rel="noopener noreferrer">console.x.ai</a></li>
                  <li><strong>Groq:</strong> Get your key from <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer">console.groq.com/keys</a></li>
                  <li><strong>Perplexity:</strong> Get your key from <a href="https://www.perplexity.ai/account/api/keys" target="_blank" rel="noopener noreferrer">perplexity.ai/settings/api/keys</a></li>
                  <li><strong>OpenRouter:</strong> Get your key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">openrouter.ai/keys</a></li>
                </ul>
                <div className="docs-note">
                  <strong>Security:</strong> Your API keys are encrypted at rest and never shared. Only you can access them.
                </div>
              </div>
            </div>

            <div className="docs-step">
              <div className="docs-step-number">4</div>
              <div className="docs-step-content">
                <h3 className="docs-step-title">Enable Models</h3>
                <p className="docs-text">
                  Go to the "Models" page in the dashboard. After adding an API key, available models will appear automatically. Toggle the models you want to use on or off.
                </p>
              </div>
            </div>

            <div className="docs-step">
              <div className="docs-step-number">5</div>
              <div className="docs-step-content">
                <h3 className="docs-step-title">Start Chatting!</h3>
                <p className="docs-text">
                  Return to Discord and use <code>/chat</code> in any text channel to create your first AI conversation thread.
                </p>
              </div>
            </div>
          </section>

          {/* Discord Commands */}
          <section id="commands" className="docs-section">
            <h2 className="docs-section-title">💬 Discord Commands</h2>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/chat</code>
                <span className="docs-command-badge">Primary</span>
              </div>
              <p className="docs-text">
                Creates a new AI conversation thread in the current channel.
              </p>
              <p className="docs-text"><strong>Options:</strong></p>
              <ul className="docs-list">
                <li><code>model</code> (optional) - Choose which AI model to use</li>
                <li><code>prompt</code> (optional) - Start the conversation with a message</li>
              </ul>
              <div className="docs-example">
                <strong>Examples:</strong>
                <ul className="docs-list">
                  <li><code>/chat</code> - Create thread with default model</li>
                  <li><code>/chat model:&lt;pick from autocomplete&gt;</code> - Create thread with a specific model</li>
                  <li><code>/chat prompt:Explain quantum computing</code> - Create thread with first message</li>
                </ul>
              </div>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/model</code>
              </div>
              <p className="docs-text">
                Change the AI model for the current thread. Must be used inside a thread created by <code>/chat</code>.
              </p>
              <p className="docs-text"><strong>Options:</strong></p>
              <ul className="docs-list">
                <li><code>model</code> (required) - Select a new model from autocomplete</li>
              </ul>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/default</code>
              </div>
              <p className="docs-text">
                Set your default model preference. This model will be used when you run <code>/chat</code> without specifying a model. This preference also applies to your private DMs with the bot.
              </p>
              <p className="docs-text"><strong>Options:</strong></p>
              <ul className="docs-list">
                <li><code>model</code> (required) - Choose a model or "last" to always use your most recent model</li>
              </ul>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/models</code>
              </div>
              <p className="docs-text">
                Lists all your enabled AI models. Shows which models are available and which is set as default.
              </p>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/usage</code>
              </div>
              <p className="docs-text">
                View usage statistics. In a thread, it shows thread stats by default. Outside a thread, it shows account stats.
              </p>
              <p className="docs-text"><strong>Options:</strong></p>
              <ul className="docs-list">
                <li><code>scope</code> (optional) - Switch between "Account" (global usage) and "Thread" (current conversation) views</li>
              </ul>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/setup</code>
              </div>
              <p className="docs-text">
                Get quick setup instructions and links to the dashboard.
              </p>
            </div>

            <div className="docs-command">
              <div className="docs-command-header">
                <code className="docs-command-name">/help</code>
              </div>
              <p className="docs-text">
                Display a list of available commands and links to documentation.
              </p>
            </div>
          </section>

          {/* API Providers */}
          <section id="providers" className="docs-section">
            <h2 className="docs-section-title">🔌 API Providers</h2>
            <p className="docs-text">
              Model availability changes frequently. After adding API keys, your dashboard lists the models you can enable.
            </p>
            <div className="docs-card">
              <h3 className="docs-card-title">OpenAI</h3>
              <p className="docs-text">
                <strong>Best For:</strong> General purpose, coding, reasoning, complex tasks
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://platform.openai.com/signup" target="_blank" rel="noopener noreferrer">Sign up at platform.openai.com</a>
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Anthropic</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Long conversations, analysis, writing, safety-conscious applications
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">Sign up at console.anthropic.com</a>
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Google</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Multimodal tasks, fast inference, cost-effective solutions
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer">Sign up at aistudio.google.com</a>
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">xAI</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Real-time information, conversational AI
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://console.x.ai" target="_blank" rel="noopener noreferrer">Sign up at console.x.ai</a>
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Groq</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Ultra-fast inference, real-time applications
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer">Sign up at console.groq.com</a>
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Perplexity</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Search-enhanced responses, research
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer">Sign up at perplexity.ai</a>
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">OpenRouter</h3>
              <p className="docs-text">
                <strong>Best For:</strong> Unified access to many community and vendor models through a single router
              </p>
              <p className="docs-text">
                <strong>Get Started:</strong> <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">Sign up at openrouter.ai</a>
              </p>
            </div>
          </section>

          {/* Managing Models */}
          <section id="models" className="docs-section">
            <h2 className="docs-section-title">🤖 Managing Models</h2>

            <div className="docs-card">
              <h3 className="docs-card-title">Enabling/Disabling Models</h3>
              <p className="docs-text">
                In the dashboard's "Models" page, you can toggle models on or off. Only enabled models appear in Discord's autocomplete when you use <code>/chat</code> or <code>/model</code>.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Setting a Default Model</h3>
              <p className="docs-text">
                Use <code>/default</code> in Discord or set it in the dashboard. Your default model is used when you create a thread without specifying a model.
              </p>
              <p className="docs-text">
                You can also choose "last" as your default, which always uses the most recent model you chatted with.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Model Settings</h3>
              <p className="docs-text">
                Click any model in the dashboard to configure:
              </p>
              <ul className="docs-list">
                <li><strong>Display Name:</strong> Customize how the model appears in Discord</li>
                <li><strong>System Prompt:</strong> Set a custom system prompt for this model only</li>
                <li><strong>Capabilities:</strong> Turn streaming, vision, image, video, or chat support on or off for that model.</li>
              </ul>
              <p className="docs-text">
                If a model doesn’t stream reliably or doesn’t actually support a mode, you can disable that capability so the UI matches what really works in practice.
                When you’re unsure, you can use <strong>Reset to Global Default</strong> in the model settings dialog to go back to the provider’s default capabilities and name.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Custom Models</h3>
              <p className="docs-text">
                Create your own specialized presets using "Custom Models". You can wrap any base model from a provider with a unique name and a permanent system instruction.
              </p>
              <ul className="docs-list">
                <li><strong>Persistent Personas:</strong> Create a character with a specific backstory, personality, or speaking style that stays "in character" across every conversation.</li>
                <li><strong>Specialized Tools:</strong> Build a dedicated assistant that strictly follows your specific formatting rules, language constraints, or coding style.</li>
              </ul>
              <p className="docs-text">
                These appear in your Discord autocomplete list just like regular models. To build one, click the <strong>Create Model</strong> button on the Models page in your dashboard.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Syncing Models</h3>
              <p className="docs-text">
                If your provider adds new models, click the "Sync" button in the dashboard to refresh your model list.
              </p>
            </div>
          </section>

          {/* Usage & Costs */}
          <section id="usage" className="docs-section">
            <h2 className="docs-section-title">📊 Usage & Costs</h2>

            <div className="docs-card">
              <h3 className="docs-card-title">How Pricing Works</h3>
              <p className="docs-text">
                OkeyBot doesn't mark up your AI costs. You pay your provider directly at their standard rates. We offer a Premium plan for unlimited bot messages—see our pricing page for current details.
              </p>
              <div className="docs-note">
                <strong>Free Tier:</strong> 20 messages per day at no cost. Great for light daily use.
              </div>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Tracking Usage</h3>
              <p className="docs-text">
                View your usage in two places:
              </p>
              <ul className="docs-list">
                <li><strong>In Discord:</strong> Use <code>/usage</code> in a thread to see that thread's costs</li>
                <li><strong>Dashboard:</strong> Visit the "Usage" page for detailed breakdowns by provider, model, and time period</li>
              </ul>
              <p className="docs-text">
                Your current Okey Credits balance is shown at the top of the dashboard, alongside your monthly usage stats.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Understanding Costs</h3>
              <p className="docs-text">
                For most chat models, costs are estimated from:
              </p>
              <ul className="docs-list">
                <li>Input tokens (your messages, context, and any attached text/code files)</li>
                <li>Output tokens (AI responses)</li>
                <li>Cached tokens (some providers offer caching discounts)</li>
              </ul>
              <p className="docs-text">
                Some search and research‑style models also apply per‑request or per‑search fees. For those, OkeyBot layers the provider’s request/search pricing on top of token costs so your <code>/usage</code> and dashboard estimates stay close to the provider’s billing.
              </p>
              <p className="docs-text">
                Each provider has different pricing. Check their official pricing pages for exact rates.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Cost Optimization Tips</h3>
              <ul className="docs-list">
                <li>Use smaller models for simple tasks</li>
                <li>Create a new <code>/chat</code> thread when you switch to a different project or topic, instead of piling everything into one long conversation.</li>
                <li>In long-running threads, use <code>/usage</code> inside the thread to see the “Context Used” percentage for the current model. If it’s high, starting a fresh thread will usually be more cost‑efficient.</li>
                <li>Enable only the models you actually use</li>
                <li>Check your usage regularly in the dashboard</li>
              </ul>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Okey Credits &amp; built‑in models</h3>
              <p className="docs-text">
                You have two options for how OkeyBot sends requests:
              </p>
              <ul className="docs-list">
                <li><strong>Your own keys:</strong> Connect provider keys (OpenAI, Anthropic, Google, xAI, Groq, Perplexity, OpenRouter) and pay them directly. This gives you access to the full range of models and modes those providers offer, without any Okey Credits limits.</li>
                <li><strong>OkeyBot built‑in:</strong> A small built‑in provider that works without setting up a key for that provider. It uses a limited set of models and modes and draws from your Okey Credits balance. If you ever run out, you can either connect your own key or upgrade to Premium to keep using built‑in models.</li>
              </ul>
              <p className="docs-text">
                Daily message limits come from your plan (Free vs Premium) and apply to all replies in Discord, whether you use built‑in models or your own keys. Okey Credits only affect the optional built‑in models. With a valid key configured, you can keep using that provider regardless of your credit balance.
              </p>
            </div>

          </section>

          {/* Memory */}
          <section id="memory" className="docs-section">
            <h2 className="docs-section-title">🧠 Memory</h2>
            <p className="docs-text">
              Memory allows OkeyBot to automatically learn and remember details about you
              across your private threads and DMs. It uses a private Discord channel as
              secure storage, so your memories stay under your control.
            </p>

            <div className="docs-card">
              <h3 className="docs-card-title">How It Works</h3>
              <p className="docs-text">
                When set up, OkeyBot uses a private <code>#okey-memory</code> channel
                in your server. This channel stores up to <strong>30 memory slots</strong> —
                concise facts about you that the AI has learned from your conversations.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Setup Guide</h3>

              <div className="docs-step">
                <div className="docs-step-number">1</div>
                <div className="docs-step-content">
                  <h3 className="docs-step-title">Enable Memory</h3>
                  <p className="docs-text">
                    Go to <strong>Dashboard → Settings</strong> and toggle the <strong>Memory</strong> option on.
                  </p>
                </div>
              </div>

              <div className="docs-step">
                <div className="docs-step-number">2</div>
                <div className="docs-step-content">
                  <h3 className="docs-step-title">Select Home Server</h3>
                  <p className="docs-text">
                    Choose a server where you are the <strong>owner</strong>. The bot will create a private memory channel here.
                  </p>
                </div>
              </div>

              <div className="docs-step">
                <div className="docs-step-number">3</div>
                <div className="docs-step-content">
                  <h3 className="docs-step-title">Create Memory Channel</h3>
                  <p className="docs-text">
                    Click <strong>"Setup #okey-memory"</strong> to automatically create a private <code>#okey-memory</code> channel in your server. The bot will configure permissions to hide it from <code>@everyone</code> and grant access to you and the bot. If a suitable private channel already exists, it will be linked automatically.
                  </p>
                </div>
              </div>

              <div className="docs-step">
                <div className="docs-step-number">4</div>
                <div className="docs-step-content">
                  <h3 className="docs-step-title">Choose Extraction Model</h3>
                  <p className="docs-text">
                    Select which AI model is used for memory extraction in the background. This can be left on <strong>Auto</strong>, which selects an enabled model from your connected providers, then falls back to a built-in OkeyBot model.
                  </p>
                  <div className="docs-note">
                    <strong>Tip:</strong> The extraction model runs silently in the background after each reply. We recommend using a fast, cheap model for this purpose.
                  </div>
                </div>
              </div>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Memory in Conversations</h3>
              <p className="docs-text">
                Once memory is set up, the AI will automatically recall relevant information
                about you across different threads and DMs. For example, if you mention your
                favorite programming language in one conversation, the AI will remember it
                in future conversations without being told again.
              </p>
              <p className="docs-text">
                You can view or delete memories in your private <code>#okey-memory</code>
                channel. To change a memory, tell OkeyBot what changed.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Limitations</h3>
              <ul className="docs-list">
                <li><strong>30 memory slots:</strong> The system stores up to 30 facts. As memories accumulate, OkeyBot curates them by combining related ones, updating what's changed, and replacing what's no longer useful, so memory gets sharper over time instead of just fuller.</li>
                <li><strong>Extraction costs:</strong> Each memory extraction call consumes tokens from your extraction model (either your own API key or Okey Credits for built-in models).</li>
                <li><strong>Server ownership required:</strong> You must be the owner of the server where the memory channel is created.</li>
                <li><strong>Memory channel must be private:</strong> The <code>#okey-memory</code> channel must have View Channel denied for <code>@everyone</code>.</li>
                <li><strong>Removing memories:</strong> Turning Memory off or unlinking the channel does not delete its messages. To permanently remove stored memories, delete them from <code>#okey-memory</code> in Discord.</li>
              </ul>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Troubleshooting</h3>
              <ul className="docs-list">
                <li><strong>Memory paused (Okey Credits):</strong> If using a built-in extraction model and you run out of Okey Credits, memory updates are paused until you add credits or switch to a model from your own API key. Existing memories can still be recalled.</li>
                <li><strong>Memory paused (API key):</strong> If using a custom extraction model and the API key is missing or invalid, memory updates are paused until you add or fix the key. Existing memories can still be recalled.</li>
                <li><strong>Bot not in server:</strong> If the bot hasn't been invited to your selected server, you'll see an invitation prompt with a link to invite it.</li>
                <li><strong>Missing permissions:</strong> The bot requires <strong>Manage Channels</strong> and <strong>Manage Roles</strong> permissions to create the private memory channel. If missing, you'll be prompted to update the bot's permissions.</li>
                <li><strong>Channel not private:</strong> A non-private existing <code>#okey-memory</code> channel will not be linked; setup will try to create a separate private channel.</li>
              </ul>
            </div>
          </section>

          {/* Advanced Features */}
          <section id="features" className="docs-section">
            <h2 className="docs-section-title">✨ Advanced Features</h2>

            <div className="docs-card">
              <h3 className="docs-card-title">Vision Models</h3>
              <p className="docs-text">
                Some models support image understanding. Simply attach an image to your Discord message in a thread, and the AI will analyze it.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Image & Video Generation</h3>
              <p className="docs-text">
                Some models generate images or short videos instead of chat text. If you start <code>/chat</code> with an image or video model, OkeyBot creates a dedicated “Image Generation” or “Video Generation” thread—send a prompt and the bot will post the generated media. Availability and pricing depend on the provider.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Streaming Responses</h3>
              <p className="docs-text">
                Most models support streaming, which means you see the response as it's generated in real-time. This makes conversations feel more natural and responsive.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Text &amp; Code Files</h3>
              <p className="docs-text">
                Premium users can attach plain-text and code files directly to their messages in threads or @mention/DM conversations. OkeyBot reads the file and treats its contents as part of your prompt.
              </p>
              <ul className="docs-list">
                <li>Supported examples: <code>.txt</code>, <code>.md</code>, <code>.json</code>, <code>.yaml</code>, <code>.js</code>, <code>.ts</code>, <code>.py</code>, <code>.go</code>, <code>.java</code>, <code>.c</code>, <code>.cpp</code>, <code>.sh</code>, and similar plain-text/code files.</li>
                <li>Unsupported: binary formats such as PDFs, Word documents, images (handled separately by vision models), archives, etc.</li>
                <li>Per-file size limit is about 250 KB. Larger files are ignored and you’ll see a message asking you to trim or split them.</li>
              </ul>
              <p className="docs-text">
                Reading attached text and code files is available on the Premium plan.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Global System Prompts</h3>
              <p className="docs-text">
                Set a global system prompt in Settings that applies to all your conversations. For example: "You are a helpful coding assistant. Always provide complete, working code examples."
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Per-Model System Prompts</h3>
              <p className="docs-text">
                Override the global prompt for specific models. Useful when you want different behavior from different AIs (e.g., creative writing with Claude, coding with GPT-4).
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Prompt Library</h3>
              <p className="docs-text">
                Create and save your own collection of reusable system prompts. Access the Prompt Library from both your Global System Prompt settings and individual Model Settings to:
              </p>
              <ul className="docs-list">
                <li>Save frequently used prompts for easy access</li>
                <li>Edit or delete your custom prompts</li>
                <li>Insert or append saved prompts into your active system prompt for a model or globally</li>
              </ul>
              <p className="docs-text">
                This helps you quickly apply consistent prompting strategies across different conversations and models without retyping.
              </p>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Context Memory</h3>
              <p className="docs-text">
                Control how much conversation history the AI remembers. This setting allows you to balance memory retention against speed and cost:
              </p>
              <ul className="docs-list">
                <li><strong>Fast (60%):</strong> Sends less history to the model. Responses are faster and generally cost less, but the AI may forget details from much earlier in the conversation.</li>
                <li><strong>Balanced (80%):</strong> The default setting. Provides a good trade-off between remembering context and maintaining performance.</li>
                <li><strong>Max (100%):</strong> Uses the model's full context window. The AI remembers as much as possible, but responses may be slower and cost more due to higher token usage.</li>
              </ul>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Thread Participation</h3>
              <p className="docs-text">
                By default, only you can chat with your AI. Enable "Allow others" in Settings to let anyone in your Discord server join your threads.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Message Editing & Regeneration</h3>
              <p className="docs-text">
                Edit your message in a thread, and the bot will regenerate its response from that point forward, clearing everything after your edited message.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Mention Mode (Server) & DMs</h3>
              <p className="docs-text">
                Outside of threads, there are two ways to get quick replies. <strong>Mention Mode (Server)</strong>: When you @mention the bot in a server, it acts as a server-wide assistant. It uses the model, API key, and billing budget of the Server Owner (configured in their dashboard). <strong>Direct Messages</strong>: In DMs, you don't need to @mention. The bot uses your personal account, your keys, and your default model preference. In both cases, reply to the bot's last message to maintain conversation context.
              </p>
            </div>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="docs-section">
            <h2 className="docs-section-title">🔧 Troubleshooting</h2>

            <div className="docs-card">
              <h3 className="docs-card-title">Bot Not Responding</h3>
              <ul className="docs-list">
                <li>Check that you've added at least one API key in the dashboard</li>
                <li>Verify your API key is valid by visiting your provider's dashboard</li>
                <li>Make sure you have at least one model enabled</li>
                <li>Try using <code>/setup</code> to see if there are any configuration issues</li>
              </ul>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Mention replies not working</h3>
              <ul className="docs-list">
                <li><strong>In Servers:</strong> The Server Owner must configure a Mention Mode model in their Dashboard Settings and have a valid connected API key or Okey Credits.</li>
                <li><strong>In DMs:</strong> Ensure you have at least one API key connected (or Okey Credits) and an active default model.</li>
                <li>Make sure you replied to a message or explicitly mentioned the bot with <code>@OkeyBot</code>.</li>
                <li>In DMs you don’t need to mention—send a message. To keep context across turns, reply to the bot’s last message.</li>
              </ul>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">"No Models Available" Error</h3>
              <ul className="docs-list">
                <li>Add an API key in the dashboard's "API Keys" page</li>
                <li>Click "Sync" on the Models page to refresh available models</li>
                <li>Enable at least one model by toggling it on</li>
              </ul>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">API Key Not Working</h3>
              <ul className="docs-list">
                <li>Double-check you copied the entire key (they're often very long)</li>
                <li>Verify the key is active in your provider's dashboard</li>
                <li>Check if your provider account has sufficient credits/billing set up</li>
                <li>Some providers require identity verification before API access</li>
              </ul>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Issues with file attachments</h3>
              <ul className="docs-list">
                <li>Reading text and code files is available on the Premium plan.</li>
                <li>Make sure the file is a plain-text or code format (for example <code>.txt</code>, <code>.md</code>, <code>.json</code>, <code>.js</code>, <code>.py</code>, <code>.go</code>). Binary formats like PDFs, Word documents, and archives are not supported.</li>
                <li>Very large files (roughly &gt;250 KB) are rejected. Split them into smaller pieces or include only the relevant sections.</li>
                <li>Files are evaluated using the account whose API key is being used (thread owner in <code>/chat</code> threads, or the conversation owner in mentions/DMs). Make sure that account has Premium and an active API key.</li>
              </ul>
            </div>
            <div className="docs-card">
              <h3 className="docs-card-title">Streaming Not Working</h3>
              <p className="docs-text">
                If streaming fails, the bot will automatically fall back to non-streaming mode. You can disable streaming for a specific model in the dashboard to avoid the error message.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">High Costs</h3>
              <ul className="docs-list">
                <li>Check which models you're using - larger models cost more</li>
                <li>Review your usage in the dashboard's "Usage" page</li>
                <li>Consider using smaller, more efficient models for simple tasks</li>
                <li>Set up billing alerts in your provider's dashboard</li>
              </ul>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Rate Limits</h3>
              <p className="docs-text">
                If you hit rate limits, that's enforced by your provider, not OkeyBot. Check your provider's dashboard to see your current limits and consider upgrading your plan with them if needed.
              </p>
            </div>

            <div className="docs-card">
              <h3 className="docs-card-title">Still Need Help?</h3>
              <p className="docs-text">
                Join our Discord support server or email us at support@okeybot.ai
              </p>
            </div>
          </section>

          {/* Footer CTA */}
          <div className="docs-cta">
            <h2 className="docs-cta-title">Ready to Get Started?</h2>
            <p className="docs-cta-text">
              Add OkeyBot to your Discord server and start chatting with the world's best AI models
            </p>
            <button className="btn btn-primary btn-hero" onClick={addToDiscord} data-umami-event="add_to_discord_docs">
              Add to Discord
            </button>
          </div>
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}
